// @flow

import React from 'react';
import AbstractNotification from './AbstractNotification';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import withBoundary from 'component/ErrorBoundary/withBoundary';
import { translate } from 'store/modules/base/i18n';
import { colors } from 'store/modules/base/ui/Tokens';
import {
  NOTIFICATION_ICON,
  NOTIFICATION_TYPE
} from 'store/modules/notifications';
import {
  CheckCircleOutlined,
  ErrorOutline,
  InfoOutlined,
  PeopleOutline,
  Person2Outlined,
  QuestionAnswerOutlined,
  WarningOutlined
} from '@mui/icons-material';

/**
 * Secondary colors for notification icons.
 *
 * @type {{error, info, normal, success, warning}}
 */
const ICON_COLOR = {
  error: colors.error06,
  normal: colors.primary06,
  success: colors.success05,
  warning: colors.warning05
};

/**
 * Implements a React {@link Component} to display a notification.
 *
 * @augments Component
 */
class Notification extends AbstractNotification {
  /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {ReactElement}
   */
  render() {
    const { t, title, titleArguments, titleKey, uid } = this.props;

    return (
      <Alert
        key={uid}
        icon={this._mapAppearanceToIcon()}
        action={this._mapAppearanceToButtons()}>
        <AlertTitle>{title || t(titleKey, titleArguments)}</AlertTitle>
        {this._renderDescription()}
      </Alert>
    );
  }

  // _getDescription: () => Array<string>;

  // _getDescriptionKey: () => string;

  // _onDismissed: () => void;

  /**
   * Creates a {@code ReactElement} for displaying the contents of the
   * notification.
   *
   * @private
   * @returns {ReactElement}
   */
  _renderDescription() {
    const description = this._getDescription();

    // Keeping in mind that:
    // - Notifications that use the `translateToHtml` function get an element-based description array with one entry
    // - Message notifications receive string-based description arrays that might need additional parsing
    // We look for ready-to-render elements, and if present, we roll with them
    // Otherwise, we use the Message component that accepts a string `text` prop
    // const shouldRenderHtml = description.length === 1 && isValidElement(description[0]);

    // the id is used for testing the UI
    return <p data-testid={this._getDescriptionKey()}>{description}</p>;
  }

  /**
   * Creates action button configurations for the notification based on
   * notification appearance.
   *
   * @param {boolean} hideErrorSupportLink - Indicates if the support link
   * should be hidden in the error messages.
   * @private
   * @returns {Object[]}
   */
  _mapAppearanceToButtons() {
    switch (this.props.appearance) {
      case NOTIFICATION_TYPE.ERROR: {
        return (
          <Button onClick={this._onDismissed}>
            {this.props.t('dialog.dismiss')}
          </Button>
        );
        // if (!hideErrorSupportLink && interfaceConfig.SUPPORT_URL) {
        //   buttons.push({
        //     content: this.props.t('dialog.contactSupport'),
        //     onClick: this._onOpenSupportLink
        //   });
        // }

        // return buttons;
      }
      case NOTIFICATION_TYPE.WARNING:
        return (
          <Button onClick={this._onDismissed}>
            {this.props.t('dialog.Ok')}
          </Button>
        );

      default:
        if (
          this.props.customActionNameKey?.length &&
          this.props.customActionHandler?.length
        ) {
          const customAction = this.props.customActionNameKey[0];
          return (
            <Button
              onClick={() => {
                if (this.props.customActionHandler[0]()) {
                  this._onDismissed();
                }
              }}
              key={customAction}>
              {this.props.t(customAction)}
            </Button>
          );
        }

        return null;
    }
  }

  /**
   * Returns the Icon type component to be used, based on icon or appearance.
   *
   * @returns {ReactElement}
   */
  _getIcon() {
    let Icon;

    switch (this.props.icon || this.props.appearance) {
      case NOTIFICATION_ICON.ERROR:
        Icon = ErrorOutline;
        break;
      case NOTIFICATION_ICON.WARNING:
        Icon = WarningOutlined;
        break;
      case NOTIFICATION_ICON.SUCCESS:
        Icon = CheckCircleOutlined;
        break;
      case NOTIFICATION_ICON.MESSAGE:
        Icon = QuestionAnswerOutlined;
        break;
      case NOTIFICATION_ICON.PARTICIPANT:
        Icon = Person2Outlined;
        break;
      case NOTIFICATION_ICON.PARTICIPANTS:
        Icon = PeopleOutline;
        break;
      default:
        Icon = InfoOutlined;
        break;
    }

    return Icon;
  }

  /**
   * Creates an icon component depending on the configured notification
   * appearance.
   *
   * @private
   * @returns {ReactElement}
   */
  _mapAppearanceToIcon() {
    const { appearance, icon } = this.props;
    const secIconColor = ICON_COLOR[appearance];
    const iconSize = 'medium';
    const Icon = this._getIcon();

    return (
      <div className={icon}>
        <div className={`ribbon ${appearance}`} />
        <Icon label={appearance} color={secIconColor} size={iconSize} />
      </div>
    );
  }
}

export default translate(withBoundary(Notification));
